import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Branch } from '../../../interfaces/entities/branch';
import { uploadImage } from '../../../services/imageUploaderService';
import { useParams, useNavigate } from 'react-router-dom';
import { BranchForm } from './branchForm';
import { defaultBranch } from '../../../defaultValues/defaultBranch';
import { LoadingIndicator } from '../../shared/alerts/loadingIndicator';
import { ErrorDisplay } from '../../shared/alerts/errorDisplay';
import { useGetBranchByIdQuery, useUpdateBranchMutation, useLazyCheckBranchUrlKeyAvailableQuery } from '../../../api/branchApi';
import { useGetCompaniesQuery } from '../../../api/companyApi';
import { useToast } from '../../../hooks/useToast';
import { useAuthenticatedUser } from '../../../hooks/useAuthenticatedUser';
import { useLoader } from '../../../hooks/useLoader';
import { getBranchImagePath } from '../../../services/storageService';

export const BranchEditContainer: React.FC = () => {
    const { user, currentUserIsOwnerOf } = useAuthenticatedUser();
    const { branchId, companyId } = useParams<{ branchId?: string, companyId?: string }>(); // Mark id as optional
    const bId = branchId ?? ""; // Ensure id is always a string
    const [branchImage, setBranchImage] = useState<File | null>(null);
    const navigate = useNavigate();

    const { data: companies = [], isLoading: loadingCompanies } = useGetCompaniesQuery();
    const { data: branch, isLoading: loadingBranch, error } = useGetBranchByIdQuery(bId);
    const [updateBranch, updateProcess] = useUpdateBranchMutation();
    const [checkUrlKey] = useLazyCheckBranchUrlKeyAvailableQuery();

    const { showSuccess, showError } = useToast();
    const { showLoader, hideLoader } = useLoader();

    const handleImageChange = (file: File | null) => {
        setBranchImage(file);
    };

    const handleCheckUrlKey = async (urlKey: string): Promise<boolean> => {
        const { data } = await checkUrlKey({ urlKey, excludeBranchId: bId });
        return data ?? false;
    };

    // Handle form submission
    const handleFormSubmit = async (values: Branch) => {
        try {
            showLoader();

            const { data: isAvailable } = await checkUrlKey({ urlKey: values.urlKey ?? '', excludeBranchId: bId });
            if (!isAvailable) {
                showError("URL Key is already in use. Please choose a different one.");
                return;
            }

            const branchData = { ...values };

            if (branchImage) {
                const imagePath = getBranchImagePath(branchData.companyId, branchData.id);
                const imageUrl = await uploadImage(imagePath, branchImage);
                branchData.image = imageUrl;
            }
            await updateBranch({
                id: bId,
                data: branchData
            }).unwrap();

            showSuccess("Branch updated successfully!");
            navigate(-1); // Navigate back to previous screen

        } catch (error: any) {
            console.error('Error while updating branch:', error);
            showError(`${error.message}`);
        } finally {
            hideLoader();
        }
    };

    const companyOptions = companies.map((company) => ({
        id: company.id, // assuming company has an `id` field
        label: company.name, // assuming company has a `name` field
    }));

    const isLoading = updateProcess.isLoading || loadingBranch || loadingCompanies;

    if (isLoading) return <LoadingIndicator message='Loading branch data...' />;
    if (error) return <ErrorDisplay message="Error fetching branch data." />;
    if (!user || !currentUserIsOwnerOf(companyId)) return <div>Unauthorized</div>;

    const initialValues: Branch = { ...defaultBranch(user.id), ...(branch || {}) };

    return (
        <Box>
            <BranchForm
                title='Edit Branch Information'
                onSubmit={handleFormSubmit}
                initialValues={initialValues}
                onImageChange={handleImageChange}
                onCheckUrlKey={handleCheckUrlKey}
                companyOptions={companyOptions}
                isSubmitting={isLoading}
                onCancel={() => navigate(-1)}
            />
        </Box>
    );
};
